import { useRouter } from "next/router";
import React, { useRef, useState } from "react";
import Head from "next/head";
import MainLayout from "../../components/MainLayout/MainLayout";
import Link from "next/link";
import Image from "next/image";
import ContentfulBlogs from "../../components/contentfulBlogs/ContentfulBlogs";
import { appServerSideProps } from "../../utils/getAppService";
import statsExplainLandingPage from "../../assets/images/stats-explained/stats-explained-landing-page.png";
import drivePredictions from "../../assets/images/stats-explained/drive-predictions.png";
import ballImage from "../../assets/images/stats-explained/ball.png";
import sqPPP2Games from "../../assets/images/stats-explained/NCAAM-sqppp-2-games.png";
import sqPPP5Games from "../../assets/images/stats-explained/NCAAM-sqppp-5-games.png";
import shotQualityShotModel from "../../assets/images/stats-explained/shotquality-shot-model.png";
import doubleTeamedLayup from "../../assets/images/stats-explained/double-teamed-layup-39pct.png";
import doubleTeamedLayupGraph from "../../assets/images/stats-explained/double-teamed-layup-39pct-graph.png";
import crowdedMidrange from "../../assets/images/stats-explained/crowded-midrange-34pct.png";
import crowdedMidrangeGraph from "../../assets/images/stats-explained/crowded-midrange-34pct-graph.png";
import openLayup98 from "../../assets/images/stats-explained/open-layup-98pct.png";
import openLayup98graph from "../../assets/images/stats-explained/open-layup-98pct-graph.png";
import openLayup from "../../assets/images/stats-explained/open-layup-94pct.png";
import openLayupGraph from "../../assets/images/stats-explained/open-layup-94pct-graph.png";
import graph1 from "../../assets/images/stats-explained/graph-1.png";
import graph2 from "../../assets/images/stats-explained/graph-2.png";
import graph3 from "../../assets/images/stats-explained/graph-3.png";
import graph4 from "../../assets/images/stats-explained/graph-4.png";
import layupShot from "../../assets/images/stats-explained/layup-shot.png";
import jumpShot from "../../assets/images/stats-explained/jump-shot.png";
import nonTransitionToTransition from "../../assets/images/stats-explained/non-transition-to-transition.png";
import downArrow from "../../assets/images/stats-explained/down-arrow.png";
import nonTransition from "../../assets/images/stats-explained/non-transition.png";
import transitionImg from "../../assets/images/stats-explained/transition.png";
import armandoLayup from "../../assets/images/stats-explained/armando-layup.png";
import kyleDunk from "../../assets/images/stats-explained/kyle-dunk.png";
import freeThrow from "../../assets/images/stats-explained/free-throw.png";
import apiPageFirstBlock from "../../assets/images/ShotQualityApiFirstBlock.gif";

const VideoPlayer = ({ src, poster }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handleVideoPlay = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="video-container cursor-pointer is-link position-relative d-flex justify-center">
      {/* add close button to right top corner */}
      <Image
        onClick={handleVideoPlay}
        src={poster}
        alt="Play Video"
        width={1920}
        height={1080}
        style={{
          width: "100%",
          maxWidth: "33rem",
          height: "auto",
          display: isPlaying ? "none" : "block",
        }}
      />
      {isPlaying && (
        <div className="d-inline-block position-relative">
          <div
            className="position-absolute is-link"
            style={{
              top: "0px",
              right: "0px",
              cursor: "pointer",
              padding: ".4rem .6rem",
              fontSize: "1rem",
              color: "white",
              background: "#00000042",
              zIndex: 3,
            }}
            onClick={() => setIsPlaying(false)}
          >
            &#10006;
          </div>
          <video
            ref={videoRef}
            width="100%"
            height="auto"
            style={{
              maxWidth: "33rem",
              marginBottom: "1rem",
              display: isPlaying ? "block" : "none",
            }}
            controls
          >
            <source src={src} type="video/mp4" />
            <source src={src.replace(".mp4", ".ogg")} type="video/ogg" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

const StatsExplainedPage = ({ user }) => {
  const router = useRouter();

  const hashName = router.asPath.split(`${router.route}`).pop();

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  //   if (hashName != "") {
  //     document.title = "ShotQuality Bets | " + hashName;
  //   } else {
  //     document.title = "ShotQuality Bets | Stats Explained";
  //   }
  // }, [hashName]);

  return (
    <>
      <Head>
        <title>The Most Predictive Data in Basketball</title>
        <meta
          name="description"
          content={
            "Dive deep into how ShotQuality uses computer vision and AI to power the most predictive shot models in basketball. "
          }
        />
        <meta property="og:title" content={"The Most Predictive Data in Basketball"} />
        <meta
          property="og:description"
          content={
            "Dive deep into how ShotQuality uses computer vision and AI to power the most predictive shot models in basketball."
          }
        />
        <meta property="og:url" content={`https://shotqualitybets.com/stats-explained`} />
      </Head>
      <MainLayout showLiveLinkHeader={false}>
        <main id="ContentBox">
          <div className="stats-explained  pb-5">
            <section className="page-content bg-black">
              <div className="container">
                <div className="row">
                  <div className="col-12 col-md-12 text-center">
                    <Image
                      src={statsExplainLandingPage}
                      alt="statsExplainLandingPage"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "auto", maxHeight: "50vh" }}
                    />
                  </div>
                  <div className="col-12 col-md-3 d-none d-md-block p-4">
                    <div style={{ borderRight: "solid 2px orange", height: "100%" }}></div>
                  </div>
                  <div className="col-12 col-md-9  mt-4">
                    <h1 className="text-white font-5">SHOT PROBABILITY MODELS EXPLAINED</h1>
                    <h4 className="text-white fw-6">
                      Dive deep into how ShotQuality uses computer vision and AI to generate player location data which
                      drives the most predictive shot models in basketball and the 5 factors which drive shot
                      probability.
                    </h4>
                    <a href="https://shotquality.com" target="_blank" className="mt-3 d-block">
                      <h5 className="text-orange">www.shotquality.com</h5>
                    </a>
                  </div>
                  <div className="col-12 text-right">
                    <Image
                      src={ballImage}
                      alt="ballImage"
                      height={500}
                      width={600}
                      style={{ height: "auto", width: "20%" }}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section className="page-content">
              <div className="container">
                <div className="row mt-5">
                  <div className="col-12 mt-4">
                    <h2 className="text-uppercase  mb-3">Shot Probability Modeling Helps Drive Predictions</h2>
                    <div className="col-12 text-center mt-3 mb-3">
                      <Image
                        src={apiPageFirstBlock}
                        alt="apiPageFirstBlock"
                        layout="responsive"
                        width={1}
                        height={1}
                        style={{ height: "auto", width: "100%", maxWidth: "60rem" }}
                      />
                    </div>
                    <p>
                      All basketball analysis tries to predict the future. We watch film and review box scores to guess
                      what happens next. Unfortunately, past results can lie to us. Players and teams have hot and cold
                      streaks. A great game or poor performance could be luck. It could also be a meaningful change in
                      ability. Elite teams have off nights, poor shooters catch fire on a random Tuesday, and the
                      basketball gods cause chaos every once in a while.
                    </p>
                    <ul className="fw-6">
                      <li>
                        <strong>
                          In 2022-23, Klay Thompson shot 33 percent from deep over the first month of the season.{" "}
                        </strong>
                        A legitimate decline or just a cold streak? He returned to form and shot 43 percent over the
                        rest of the season.
                      </li>
                      <li>
                        <strong>
                          Over the first five seasons of his career, Andre Drummond only made 38 percent of his free
                          throws.{" "}
                        </strong>
                        In 2017-18, that number jumped up to 61 percent. Did he improve in the offseason, or just get
                        luck for a few months? In the five seasons since then, he made 57 percent of his free throws.
                        That 61 percent was not a fluke.
                      </li>
                      <li>
                        <strong>
                          Marcus Morris shot just under 37 percent from three in his first nine seasons, then jumped up
                          to 47 percent in 2020-21.{" "}
                        </strong>
                        What happened? Did Morris unlock better shooting touch over the summer? It seems like the season
                        was random, he shot just under 37 percent in the two seasons since.
                      </li>
                    </ul>
                    <h4 className="fw-6 text-center mb-3 mt-5">
                      “Unfortunately, past results can lie to us. Players and teams have hot and cold streaks.{" "}
                      <strong className="text-orange">A great game or poor performance could be luck.</strong>”
                    </h4>
                    <p>
                      In the moment we do not know if our eyes and the box score lies to us or tells the truth. Here is
                      where our shot probability model steps in to quantify regression. It analyzes the game at the
                      building blocks of basketball: shots. If we work to predict the likelihood that every shot is
                      made, we can imagine that a single game occurs thousands of times, or a player takes millions of
                      the same shot. Increasing the prediction if teams or players beat it, and decreasing the it if
                      they don’t. We then learn how good players and teams actually are. And if we know exactly how good
                      each player and team is, we can make better predictions about their futures.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-5 mb-2">
                    <h2 className="text-uppercase ">How ShotQuality’s Model Predicts Player Performance</h2>
                  </div>
                  <div className="col-12 text-center mt-4 mb-3">
                    <Image
                      src={sqPPP5Games}
                      alt="sqPPP5Games"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "36rem" }}
                    />
                  </div>
                  <div className="col-12">
                    <p>
                      To summarize player’s offensive efficiency, we simple ask, “how many points do you score per
                      scoring opportunity?” Points per possession (true shooting attempt) cuts to the core of a player’s
                      offensive game. The black line on the chart below shows the traditional approach, showing the
                      correlation coefficient between the player’s season performance and their performance over the
                      rest of the season. After one game, it is quite low, trying to predict the result of 30 games from
                      such a small sample is difficult. At the halfway point of the season, traditional PPP’s predictive
                      ability improves, since it has a larger sample size, while predicting a large enough sample over
                      the back half of the season to smooth out variance that could occur in one or two games. However,
                      the ShotQuality PPP (SQ PPP) is consistently a better predictor throughout the entire season.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-5">
                    <h2 className="text-uppercase ">How ShotQuality’s Models’ Predicts Team Performance</h2>
                  </div>
                  <div className="col-12 text-center mt-4 mb-2">
                    <Image
                      src={sqPPP2Games}
                      alt="sqPPP2Games"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "36rem" }}
                    />
                  </div>
                  <div className="col-12">
                    <p>
                      We observe a similar phenomenon at the team level. Looking at the first twenty games of the
                      season, ShotQuality points per possession is more predictive of the rest of the season than the
                      team’s own performance.
                    </p>
                    <h2 className="text-uppercase  mt-5 mb-2">How Do We Model Shot Probability? The Starting 5</h2>
                    <p>
                      The ShotQuality shot probability model has five main components and uses up to 100 variables in
                      total between each of these sections:
                    </p>
                    <ul className="fw-6">
                      <li className="mb-2">
                        <strong>Defensive Distance:</strong> How does defensive ability and positioning impact the shot?
                        I.e. the closest defender is eight feet away, there are three defenders within five feet of the
                        defender, Houston plays elite defense, Delaware State plays poor defense.
                      </li>
                      <li className="mb-2">
                        <strong>Shooter Ability and Player Performance:</strong> How good is the shooter? I.e. Stephen
                        Curry is an elite three-point shooter, Josh Richardson is average, and Russell Westbrook is
                        poor.
                      </li>
                      <li className="mb-2">
                        <strong>Play Type Descriptors:</strong> What happened before the shot? I.e. transition,
                        pick-and-roll, cut.
                      </li>
                      <li className="mb-2">
                        <strong>Shot Type Descriptors:</strong> What happened during the shot? I.e. dunk,
                        catch-and-shoot, off-the-dribble, shot distance.
                      </li>
                      <li className="mb-2">
                        <strong>Key Inferences:</strong> How can we improve predictions while missing data? I.e. late
                        shot clock shots tend to be rushed, players at Gonzaga tend to be good shooters, tall players
                        tend to be poor free throw shooters.
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-5 mb-2">
                    <h2 className="text-uppercase ">The Value of ShotQuality’s Shot Model</h2>
                  </div>
                  <div className="col-12 text-center mb-2">
                    <Image
                      src={shotQualityShotModel}
                      alt="shotQualityShotModel"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "42rem" }}
                    />
                  </div>
                  <div className="col-12">
                    <p>
                      The goal of any basketball analysis is to learn from the past and make predictions about the
                      future. At ShotQuality, our chosen weapon in this battle against space and time is our shot
                      probability model. Our model not only out-predicts traditional statistics, it also makes these
                      predictions earlier, making it better and smarter for both players and teams
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-5 mb-2">
                    <h2 className="text-uppercase ">#1: Defensive Distance - Location Data is Key</h2>
                  </div>

                  <div className="col-12">
                    <p>
                      Defense is notoriously difficult to measure in all sports, especially basketball. A great rim
                      protector deters drives to the basket. A lockdown perimeter defender is never challenged. At
                      ShotQuality, we collect proprietary player location information to improve out model and analysis,
                      along with modern approaches to classical team defensive analysis. Considerations for defense in
                      the shot probability model fall into two groups:
                    </p>

                    <ol className="fw-6">
                      <li className="mb-2">
                        <strong>ShotQuality location data:</strong> where is the shooter and where are the defenders?
                      </li>
                      <li className="mb-2">
                        <strong>Primary defender:</strong> what characteristics do we know about the primary defender?
                      </li>
                    </ol>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt-5 mb-2">
                    <h2 className="text-uppercase ">ShotQuality Location Data</h2>
                  </div>

                  <div className="col-12">
                    <p>
                      A unique component of our shot model is the incorporation of our own proprietary data with the
                      location and identity of every single player on the court at the moment of the shot that we create
                      in-house using computer vision and AI.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Location Data Measures Defender Distance</h2>
                    <p>
                      The most obvious benefit of location data is the distance to the nearest defender or group of
                      defenders. A tightly contested shot is more difficult than an open shot. A layup contested by a
                      single player is easier to make than a shot contested by two or three players.
                    </p>
                  </div>
                  <div className="col-12 col-md-6 text-center mt-4 mb-2">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/Defense_double_teamed_layup.mp4"
                      poster={doubleTeamedLayup}
                    />

                    <Image
                      className="mt-2"
                      src={doubleTeamedLayupGraph}
                      alt="doubleTeamedLayupGraph"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "31rem", marginBottom: "1rem" }}
                    />
                  </div>
                  <div className="col-12 col-md-6 text-center mt-4 mb-2">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/Defense_Kalkbrenner_open_layup.mp4"
                      poster={openLayup}
                    />
                    <Image
                      className="mt-2"
                      src={openLayupGraph}
                      alt="openLayupGraph"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "31rem", marginBottom: "1rem" }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Location Data Measures Positioning</h2>
                    <p>
                      A defender between the shooter and the basket is most impactful. A driving layup with the defender
                      behind the player is nearly an open layup, even if the defender is close. A defender on the side
                      of a driving player is out of position, which yields some advantage to the offensive player.
                    </p>
                  </div>
                  <div className="col-12 pt-5 pb-5">
                    <table className="sqppp-ratings-and-table text-center">
                      <thead>
                        <tr>
                          <th>Closest Defender Location</th>
                          <th>NCAAM Layup FG%</th>
                          <th>NCAAM Dunk FG%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Front</td>
                          <td>46%</td>
                          <td>79%</td>
                        </tr>
                        <tr>
                          <td>Side</td>
                          <td>53%</td>
                          <td>87%</td>
                        </tr>
                        <tr>
                          <td>Behind</td>
                          <td>63%</td>
                          <td>93%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-center">Actual Field Goal Percentage by Defender Positioning 2022-24</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Location Data Measures Crowding Effect</h2>
                    <p>
                      There is significant effect that the mere presence of defenders harms a player’s ability to make a
                      shot, even without a contest. In a crowded lane or midrange area, a shooter tends to rush or alter
                      their shot in anticipation of defensive action. Even when no contest occurs, a shooter lowers
                      their ability to make a shot by anticipating this defensive action.
                    </p>
                  </div>
                  <div className="col-12 col-md-6 text-center mt-4 mb-2">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/Defense_Crowding_crowded_shot.mp4"
                      poster={crowdedMidrange}
                    />
                    <Image
                      className="mt-2"
                      src={crowdedMidrangeGraph}
                      alt="crowdedMidrangeGraph"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "31rem", marginBottom: "1rem" }}
                    />
                  </div>
                  <div className="col-12 col-md-6 text-center mt-4 mb-2">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/non_crowded_layup.mp4"
                      poster={openLayup98}
                    />

                    <Image
                      className="mt-2"
                      src={openLayup98graph}
                      alt="openLayup98graph"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "31rem", marginBottom: "1rem" }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Location Data Identifies the Primary Defender</h2>
                    <p>
                      The primary defender on a shot attempt alters a player’s ability to get into a good position for a
                      rhythm shot in two ways: the defender’s height/length and the defender’s position. Tall defenders
                      are more difficult to shoot over, especially impactful for inside shots. Tall defenders on the
                      perimeter may deter a shot attempt from occurring, but they defend plenty of shots at the rim, and
                      each additional inch makes it more difficult for offensive players to finish. Defender position
                      also matters, if a slow and stiff center guards a three on the perimeter, they allow the shooter
                      to more easily create space off the dribble or more time to release their shot. Similarly, a guard
                      defending an inside layup may struggle, not only because of their height, but because of lack of
                      experience defending that type of shot.
                    </p>
                  </div>
                  <div className="col-12 pt-5 pb-5">
                    <table className="sqppp-ratings-and-table text-center">
                      <thead>
                        <tr>
                          <th>Closest Defender Height</th>
                          <th>NCAAM Layup FG%</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>&lt; 6-0</td>
                          <td>68%</td>
                        </tr>
                        <tr>
                          <td>&lt; 6-6</td>
                          <td>57%</td>
                        </tr>
                        <tr>
                          <td>&lt; 7-0</td>
                          <td>51%</td>
                        </tr>
                        <tr>
                          <td>&gt;= 7-0</td>
                          <td>48%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-center">Field Goal Percentage by Closest Defender Height 2022-24 Season NCAAM</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">#2 Shooter Ability and Player Performance</h2>
                    <p>
                      We incorporate player ability by estimating their performance on an average shot for each shot
                      type, this estimate blends three components: Player performance, Shot type and League performance.
                    </p>
                    <p>
                      For a given shot type, we analyze a player’s entire career, with greater weight on recent
                      performance. This means if a player improves or declines as a shooter, his or her ability estimate
                      updates accordingly, but such changes demand proof. A single hot or cold streak cannot throw our
                      predictions off. During a player’s freshman year in college, every shot changes their ability
                      estimate since we knew so little about him. By the time the same player plays in his tenth NBA
                      season, his rookie season shots have very little weight compared to the last few seasons.
                    </p>
                  </div>
                  <div className="col-12 col-md-6">
                    <p className="mt-4">
                      Jayson Tatum entered the league on fire, reflected by his true three-point ability estimate
                      rocketing up to 40 percent during his rookie season. Since then, his three-point shooting steadily
                      declined, making only 35 percent of his shots from beyond the arc these past two seasons. His
                      ability estimate is still around 37 percent, since we believe he is better than the recent 35
                      percent performances, but we have seen enough evidence that he is not the 40-plus percent shooter
                      we thought he was.
                    </p>
                  </div>
                  <div className="col-12 col-md-6 text-center">
                    <Image
                      src={graph1}
                      alt="graph1"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "30rem", marginBottom: "1rem" }}
                    />
                  </div>
                  <div className="col-12 col-md-6 text-center">
                    <h2 className="mb-2">Mikal Bridges driving layup</h2>
                    <Image
                      src={graph2}
                      alt="graph2"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "30rem", marginBottom: "1rem" }}
                    />
                  </div>
                  <div className="col-12 col-md-6 text-center">
                    <h2>2018 NBA Draft Class Threes</h2>
                    <Image
                      src={graph3}
                      alt="graph3"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "30rem", marginBottom: "1rem" }}
                    />
                  </div>
                  <div className="col-12 col-md-12 text-center">
                    <h2>2018 NBA Draft Class Drives</h2>
                    <Image
                      src={graph4}
                      alt="graph4"
                      height={1080}
                      width={1920}
                      style={{ height: "auto", width: "100%", maxWidth: "30rem", marginBottom: "1rem" }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">League Performance</h2>
                    <p>
                      Next we calculate the league’s performance level. In the 1990s, mens college basketball players
                      made 67 percent of their free throws. Over time, players improved as shooters, making over 71
                      percent of free throws in the last six seasons. We observe the league’s level of performance as a
                      starting point for all player estimates. Sometimes league performance reflects changes to the
                      game, like moving the three-point line or illegal defense rules. This also reflects changes in the
                      general ability of players for a specific shot type, jump-shooting skill increased in the past
                      twenty years, but post-up skill declined.
                    </p>
                  </div>
                  <div className="col-12 pt-5 pb-5">
                    <table className="sqppp-ratings-and-table text-center">
                      <thead>
                        <tr>
                          <th>Season</th>
                          <th>NBA Layup</th>
                          <th>NBA 3PT</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>2019</td>
                          <td>55%</td>
                          <td>35%</td>
                        </tr>
                        <tr>
                          <td>2020</td>
                          <td>54%</td>
                          <td>36%</td>
                        </tr>
                        <tr>
                          <td>2021</td>
                          <td>55%</td>
                          <td>37%</td>
                        </tr>
                        <tr>
                          <td>2022</td>
                          <td>56%</td>
                          <td>35%</td>
                        </tr>
                        <tr>
                          <td>2023</td>
                          <td>57%</td>
                          <td>36%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="mt-4">
                      We then blend league-average performance with the player ability estimate. For players with many
                      shots, their own performance makes up the majority of the shooter ability component of the model.
                      For players with a small number of shots, however, padding with league ability is vital. A
                      freshman attempting a jump shot may have insufficient three-point attempts to accurately measure
                      ability. Other players may have very little playing time overall, such as NBA rookies and college
                      freshman. With little to no performance history for those players, we optimize our predictions by
                      assuming these players are league-average.{" "}
                    </p>
                  </div>
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Shot Type</h2>
                    <p>
                      This blending varies by shot type. Three-point shots depend on heavily shooter skill, so we weigh
                      league-average performance lightly. Tip-ins, on the other hand, are quite random, meaning we weigh
                      league-average performance heavily, since we expect player performance to regress towards the
                      league mean.
                    </p>
                    <p>
                      In addition to using a shot type’s shooter ability estimate, we use estimates for other shot types
                      as well. Free throw ability indicates shooting touch, which improves jump shot predictions. Layups
                      and dunks both depend on athleticism and finishing ability, so they both support each other’s
                      predictions.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">#3: Play Type Descriptors</h2>
                    <p>
                      A play type describes the events before the shooting motion begins. Here is a quick list of
                      example play types: Pick-and-roll, Cut, Transition, Post-up, Off-ball screen, and Drive.
                    </p>
                    <p>
                      With each play types, we do not know if the shot was a jump shot or a layup, but we learn
                      important context for the shot. Transition shots generally face lower defensive pressure, cutting
                      players receive a pass if their teammate sees an easy opportunity for them, pick-and-rolls create
                      mismatches. These inferences and others made from the data improve model predictions. We observe
                      how performance changes among similar shots, but with different play types.
                    </p>
                    <div className="overflow-auto">
                      <table className="sqppp-ratings-and-table mt-5">
                        <thead>
                          <tr>
                            <th>Shot Type</th>
                            <th>Play Type</th>
                            <th>NCAAM</th>
                            <th>NCAAW</th>
                            <th>NBA</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td rowSpan={4}>
                              <h3>Layups</h3>
                              <div className="text-center mt-2">
                                <Image
                                  src={layupShot}
                                  alt="layupShot"
                                  height={500}
                                  width={500}
                                  style={{ height: "auto", width: "100%", maxWidth: "7rem", marginBottom: "1rem" }}
                                />
                              </div>
                            </td>
                            <td>Driving</td>
                            <td>47%</td>
                            <td>43%</td>
                            <td>50%</td>
                          </tr>
                          <tr>
                            <td>Non-Driving</td>
                            <td>57%</td>
                            <td>54%</td>
                            <td>59%</td>
                          </tr>
                          <tr>
                            <td>Cuts</td>
                            <td>64%</td>
                            <td>63%</td>
                            <td>63%</td>
                          </tr>
                          <tr>
                            <td>Non-Cuts</td>
                            <td>54%</td>
                            <td>53%</td>
                            <td>54%</td>
                          </tr>
                          <tr>
                            <td rowSpan={4}>
                              <h3>Jump Shots</h3>
                              <div className="text-center mt-2">
                                <Image
                                  src={jumpShot}
                                  alt="jumpShot"
                                  height={500}
                                  width={500}
                                  style={{ height: "auto", width: "100%", maxWidth: "7rem", marginBottom: "1rem" }}
                                />
                              </div>
                            </td>
                            <td>Transition</td>
                            <td>36%</td>
                            <td>34%</td>
                            <td>37%</td>
                          </tr>
                          <tr>
                            <td>Non-Transition</td>
                            <td>34%</td>
                            <td>32%</td>
                            <td>36%</td>
                          </tr>
                          <tr>
                            <td>Pick-and-Roll</td>
                            <td>36%</td>
                            <td>33%</td>
                            <td>39%</td>
                          </tr>
                          <tr>
                            <td>Non-Pick-and-Roll</td>
                            <td>34%</td>
                            <td>31%</td>
                            <td>37%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-center">Actual Field Goal Percentage by Play Type 2015-2023</p>
                  </div>
                  <div className="col-12 col-md-12 text-center mt-5 mb-3 position-relative">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/PlayType_Non_transition_3.mp4"
                      poster={nonTransition}
                    />
                    {/* <br />
                    <br />
                    <br /> <br /> <br /> */}
                    <div className="text-left pl-3" style={{ maxWidth: "33rem", margin: "auto" }}>
                      <Image
                        src={downArrow}
                        alt="downArrow"
                        height={1080}
                        width={1920}
                        style={{
                          height: "auto",
                          width: "100%",
                          maxWidth: "4.4rem",
                          // top: "39%",
                          // left: "31%",
                          // zIndex: 2,
                          // position: "absolute",
                        }}
                      />
                    </div>
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/PlayType_Transition_3.mp4"
                      poster={transitionImg}
                    />
                  </div>
                  <div className="col-12">
                    <p>
                      In both plays, Nembhard shoots a moving three with similar defensive coverage. However, he
                      receives a{" "}
                      <strong>+4.8 percent increase in the second shot because it occurs in transition.</strong> The
                      model learns that transition offenses face less organized defenses without defined matchups. In
                      this play, the contesting defender scrambles late to disrupt an open shooter. In the first play,
                      Villanova switches, but Justin Moore confidently closes out, knowing that his teammates are in
                      defensive position. Nembhard then rushes his shot a bit to get if off before Moore shuts down the
                      opportunity.
                    </p>
                    <p>
                      Knowing that a shot is taken in transition unlocks all of these details not explicitly captured.
                      As always with models, this is a generality, and we take care to properly handle the true
                      complexity of these actions.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">#4: Shot Type Descriptors</h2>
                    <p>
                      Shot types descriptors describes the shooting motion of a field goal attempt. A shot type is a
                      broad basketball name for a shot, like jump shot, dunk, layup, or hook shot.
                    </p>

                    <table className="sqppp-ratings-and-table text-center">
                      <thead>
                        <tr>
                          <th>Shot Type</th>
                          <th>NCAAM</th>
                          <th>NCAAW</th>
                          <th>NBA</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Jump Shot</td>
                          <td>34%</td>
                          <td>31%</td>
                          <td>38%</td>
                        </tr>
                        <tr>
                          <td>Dunk</td>
                          <td>87%</td>
                          <td>N/A</td>
                          <td>88%</td>
                        </tr>
                        <tr>
                          <td>Layup</td>
                          <td>56%</td>
                          <td>54%</td>
                          <td>57%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-center">Actual Field Goal Percentage by Shot Type 2015-2023</p>
                    <p className="mt-5">
                      A shot descriptor gives additional detail to the shot type, like wide open, catch-and-shoot, or
                      right hand. Knowledge of the shot type sharpens model predictions. The average dunk is made more
                      often than the average jump shot. The average field goal percentage of a shot type determines the
                      starting point for all predictions. Shot type descriptors further decompose shot types, an
                      off-the-dribble three is made less often than a catch-and-shoot three, and a contested layup is
                      made less often than an open layup.
                    </p>
                    <p>
                      Knowing that a shot is taken in transition unlocks all of these details not explicitly captured.
                      As always with models, this is a generality, and we take care to properly handle the true
                      complexity of these actions.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">🎬 Shot Type Descriptor Film Review</h2>
                  </div>
                  <div className="col-12 col-md-6 text-center  mb-3">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/ShotTypeDescriptor_Layup_Armando_Bacot.mp4"
                      poster={armandoLayup}
                    />
                  </div>
                  <div className="col-12 col-md-6 text-center  mb-3">
                    <VideoPlayer
                      src="https://shotquality-use1-bets-public.s3.amazonaws.com/Dunk_Kyle.mp4"
                      poster={kyleDunk}
                    />
                  </div>
                  <div className="col-12">
                    <p>
                      In the first play, Armando Bacot misses a layup that we gave a 71 percent chance of going in,
                      while Kyle Filipowski went up strong for a dunk that we predict he makes 93 percent of attempts.
                      Both shots occur near the basket in a congested restricted area, but the taller Filipowski goes up
                      with two hands and leaves no room for a defender between himself and the basket. Bacot, on the
                      other hand, releases from one hand leaning away from the basket, giving him worse control over the
                      ball and more room for defenders to alter his shot.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">#5: Key Inferences</h2>
                    <p>
                      Sometimes we do not know a lot of information about a player, like college freshman, NBA rookies,
                      and international players. Other times a player experiences a dramatic change, like transferring
                      or improving over the offseason. In these instances, our shooter ability estimates can’t tell the
                      full story. For every elite shooting freshman that makes their first few threes of the season,
                      another poor shooting freshman that started with luck and shot three-for-three. In the moment, we
                      cannot know who is who. This is where some key inferences come into play.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Tall Players are Bad Shooters</h2>
                    <p>
                      The worst free throw shooters in NBA history are mostly centers. The best free throw shooters in
                      NBA history are mostly guards. When a player has a short or non-existent history, their height and
                      position provide valuable information. A seven-foot center is probably not a knockdown shooter,
                      and a six-foot guard is probably an okay shooter. That same center is probably an okay layup
                      maker, and the same guard probably struggles inside.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Free Throw Percentage</h2>
                    <table className="sqppp-ratings-and-table mt-5 text-center">
                      <thead>
                        <tr>
                          <th>HEIGHT</th>
                          <th>NBA</th>
                          <th>NCAAM</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Under 6 feet</td>
                          <td>81%</td>
                          <td>77%</td>
                        </tr>
                        <tr>
                          <td>6-0 to 6-6 </td>
                          <td>80%</td>
                          <td>74%</td>
                        </tr>
                        <tr>
                          <td>6-7 to 6-11</td>
                          <td>74%</td>
                          <td>67%</td>
                        </tr>
                        <tr>
                          <td>Over 7 feet</td>
                          <td>71%</td>
                          <td>63%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-center">Free Throw Percentage by Height 2015-2023</p>
                    <div className="text-center">
                      <Image
                        src={freeThrow}
                        alt="freeThrow"
                        height={500}
                        width={600}
                        style={{ height: "auto", width: "100%", maxWidth: "24rem", marginBottom: "1rem" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Layup Field Goal Percentage</h2>
                    <table className="sqppp-ratings-and-table mt-5 text-center">
                      <thead>
                        <tr>
                          <th>HEIGHT</th>
                          <th>NBA</th>
                          <th>NCAAM</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Under 6 feet</td>
                          <td>52%</td>
                          <td>51%</td>
                        </tr>
                        <tr>
                          <td>6-0 to 6-6 </td>
                          <td>54%</td>
                          <td>53%</td>
                        </tr>
                        <tr>
                          <td>6-7 to 6-11</td>
                          <td>55%</td>
                          <td>57%</td>
                        </tr>
                        <tr>
                          <td>Over 7 feet</td>
                          <td>57%</td>
                          <td>58%</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-center">Layup Field Goal Percentage by Shooter Height 2015-2023</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Good Teams Take Good Shots</h2>

                    <p>
                      Gonzaga, South Dakota, and Colgate have consistently been amongst the top three-point shooting
                      teams in the country each year. This is despite constant roster turnover with recruits, transfers,
                      graduates, and draftees. Teams achieve this with superior coaching, talent identification, and
                      scheme. When a player joins an elite three-point shooting team, they are probably an above-average
                      shooter, otherwise these teams would not continue their success with different rosters.
                    </p>
                    <p>
                      We extend this to other shot types. Purdue’s men tend to have good bigs, so a new center is
                      probably above-average. Small schools tend to have smaller or less talented bigs, so their power
                      forwards are probably below-average. Villanova tends to have skilled point guards, so freshman are
                      probably above-average.
                    </p>
                    <p>
                      There are exceptions to these guidelines. We do not assume a new player on Gonzaga is instantly a
                      40 percent three-point shooter, but a player’s team and conference gives us valuable information
                      about their ability.
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Good Players Take Many Shots</h2>

                    <p>
                      Lastly, good players take more shots than bad players. Duh. This reflects their ability, good
                      players should take more shots. Poor players taking many shots lose their attempts to the better
                      players fairly quickly. That is the key here. Players with more shots are probably better scorers
                      for a given shot type. Shooting volume reflects a player’s own confidence, and the information
                      known by their teammates and coaches, who observe a much larger sample size in practice and
                      shoot-arounds. Fans and analysts see very few shots compared to the hundreds or thousands of shots
                      taken in private.
                    </p>
                    <p>
                      In Brook Lopez’s first eight NBA seasons, he attempted 31 threes, making 3. In 2016-17, he took 35
                      in his first seven games, making 11! How? What happened? Lopez clearly spent long hours in the gym
                      working on his jump shot, something unseen by the public, but well known by his teammates and
                      coaches who saw his improvement every day. They gave him the green light from deep as he developed
                      into a premier 3-and-D center.
                    </p>
                    <p>
                      From a small sample of 35 shots alone, we would assume a player is poor shooter, but the sudden
                      spike in utilization beyond the arc signaled to us that Lopez is probably an improved shooter, and
                      our predictions update accordingly.
                    </p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <h2 className="text-uppercase  mt-5 mb-2">Putting it All Together</h2>
                    <p>
                      ShotQuality’s unique data for NCAAM, NCAAW, NBA, and WNBA games power the most predictive shot
                      models in basketball. Put ShotQuality’s models, regression analysis, and trends which are more
                      predictive to work for your strategy.
                    </p>
                    {/* <p>
                      Learn more about the ShotQuality by visiting{" "}
                      <a href="https://shotquality.com" target="_blank" className="text-orange">
                        www.shotquality.com
                      </a>
                    </p> */}
                  </div>
                </div>
                {/* ////////////////////////// */}
              </div>
              <div
                className="contentful-blogs-section"
                style={{
                  // backgroundImage: `url(${bgDotted.src})`,
                  // objectFit: "round",
                  backgroundColor: "#1C2330",
                }}
              >
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <ContentfulBlogs user={user} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </MainLayout>
    </>
  );
};
export const getServerSideProps = appServerSideProps(async ({ appService }) => {
  const isUserLogged = appService.getUser();

  let user = null;

  if (isUserLogged) {
    user = await appService.get("api/account");
  }
  return { props: { user } };
});
export default StatsExplainedPage;
